/**
 * URL validation utilities with proper TLD checking
 */

// Common valid TLDs (regularly updated list of most used TLDs)
// This is not exhaustive but covers 99%+ of legitimate use cases
const VALID_TLDS = new Set([
  // Generic TLDs
  "com",
  "org",
  "net",
  "edu",
  "gov",
  "mil",
  "int",
  "info",
  "biz",
  "name",
  "pro",
  "museum",
  "aero",
  "coop",
  "jobs",
  "travel",
  "mobi",
  "cat",
  "asia",
  "tel",
  "xxx",
  "post",

  // Common new gTLDs
  "app",
  "dev",
  "io",
  "co",
  "me",
  "ai",
  "tech",
  "online",
  "site",
  "website",
  "store",
  "shop",
  "blog",
  "cloud",
  "digital",
  "network",
  "agency",
  "studio",
  "design",
  "media",
  "news",
  "email",
  "web",
  "xyz",
  "top",
  "live",
  "life",
  "world",
  "space",
  "today",
  "zone",
  "link",
  "click",
  "page",
  "tools",
  "solutions",
  "services",
  "group",
  "global",
  "company",
  "center",
  "support",
  "systems",
  "technology",

  // Country code TLDs (most common)
  "ir", // Iran
  "us", // United States
  "uk", // United Kingdom
  "de", // Germany
  "fr", // France
  "es", // Spain
  "it", // Italy
  "nl", // Netherlands
  "be", // Belgium
  "at", // Austria
  "ch", // Switzerland
  "pl", // Poland
  "ru", // Russia
  "cn", // China
  "jp", // Japan
  "kr", // South Korea
  "in", // India
  "au", // Australia
  "nz", // New Zealand
  "ca", // Canada
  "br", // Brazil
  "mx", // Mexico
  "ar", // Argentina
  "za", // South Africa
  "ae", // UAE
  "sa", // Saudi Arabia
  "tr", // Turkey
  "eg", // Egypt
  "pk", // Pakistan
  "id", // Indonesia
  "my", // Malaysia
  "sg", // Singapore
  "th", // Thailand
  "vn", // Vietnam
  "ph", // Philippines
  "tw", // Taiwan
  "hk", // Hong Kong
  "se", // Sweden
  "no", // Norway
  "dk", // Denmark
  "fi", // Finland
  "pt", // Portugal
  "gr", // Greece
  "cz", // Czech Republic
  "hu", // Hungary
  "ro", // Romania
  "ua", // Ukraine
  "il", // Israel
  "ie", // Ireland

  // Common multi-level TLDs (second level)
  "co.uk",
  "co.ir",
  "co.jp",
  "co.kr",
  "co.nz",
  "co.za",
  "co.in",
  "co.id",
  "com.au",
  "com.br",
  "com.mx",
  "com.ar",
  "com.tr",
  "com.cn",
  "com.tw",
  "com.hk",
  "com.sg",
  "com.my",
  "org.uk",
  "org.au",
  "net.au",
  "ac.uk",
  "ac.ir",
  "gov.uk",
  "edu.au",
]);

/**
 * Extract the TLD from a hostname
 * Handles both simple TLDs (com) and multi-level TLDs (co.uk)
 */
function extractTLD(hostname: string): string | null {
  if (!hostname) return null;

  const parts = hostname.toLowerCase().split(".");
  if (parts.length < 2) return null;

  // Check for multi-level TLDs first (e.g., co.uk, com.au)
  if (parts.length >= 3) {
    const twoLevelTld = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    if (VALID_TLDS.has(twoLevelTld)) {
      return twoLevelTld;
    }
  }

  // Return single-level TLD
  return parts[parts.length - 1];
}

/**
 * Check if a TLD is valid
 */
export function isValidTLD(tld: string): boolean {
  return VALID_TLDS.has(tld.toLowerCase());
}

/**
 * Validate a URL string
 * @param urlString - The URL to validate
 * @param options - Validation options
 * @returns true if the URL is valid, false otherwise
 */
export function isValidUrl(
  urlString: string,
  options: {
    requireProtocol?: boolean;
    allowedProtocols?: string[];
    checkTLD?: boolean;
  } = {},
): boolean {
  const {
    requireProtocol = false,
    allowedProtocols = ["http:", "https:"],
    checkTLD = true,
  } = options;

  if (!urlString || typeof urlString !== "string") {
    return false;
  }

  // Trim whitespace
  let url = urlString.trim();

  // Add protocol if missing and not required
  if (!requireProtocol && !/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  try {
    const parsedUrl = new URL(url);

    // Check protocol
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return false;
    }

    // Check hostname exists
    if (!parsedUrl.hostname) {
      return false;
    }

    // Check TLD validity
    if (checkTLD) {
      const tld = extractTLD(parsedUrl.hostname);
      if (!tld || !isValidTLD(tld)) {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Normalize a URL (add protocol if missing, trim whitespace)
 * @param urlString - The URL to normalize
 * @returns The normalized URL or null if invalid
 */
export function normalizeUrl(urlString: string): string | null {
  if (!urlString || typeof urlString !== "string") {
    return null;
  }

  let url = urlString.trim();

  // Add protocol if missing
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.href;
  } catch {
    return null;
  }
}
